import { useMemo, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CaretLeft as ChevronLeft,
  Briefcase,
  CalendarBlank as Calendar,
  TrendUp as TrendingUp,
  Trophy as Award,
  Envelope as Mail,
  Phone,
  Globe,
  Scales as Scale,
  Wallet,
  Gavel,
  Users,
  Plus,
  Trash,
  Star,
} from '@phosphor-icons/react';
import { StatCard } from '@/components/shared/StatCard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { fetchBarrister } from '@/lib/barristersApi';
import { useMattersStore } from '@/lib/mattersStore';
import { useHearingsStore } from '@/lib/hearingsStore';
import { useDiaryStore, HOURS, dateKey, TONE_STYLE, KIND_LABEL, todayKey as getTodayKey } from '@/lib/diaryStore';
import { useAuthStore } from '@/features/auth/store';
import { useClerkWorkspace } from '@/lib/useClerkWorkspace';
import { canSeeEventContent, canSeeMatter, isAdmin, redactedEventLabel, type PrincipalLite } from '@/lib/permissions';
import {
  fetchClerksForBarrister,
  fetchAvailableClerks,
  assignClerkToBarrister,
  removeClerkFromBarrister,
} from '@/lib/barristerClerksApi';

type Tab = 'calendar' | 'practice' | 'matters' | 'hearings' | 'clerks';

const ROLE_LABEL: Record<string, string> = {
  silk: 'KC / Silk',
  junior: 'Junior',
  pupil: 'Pupil',
  door_tenant: 'Door tenant',
};

const AVAILABILITY_LABEL: Record<string, string> = {
  available: 'Available',
  partial: 'Partial',
  busy: 'In court',
  on_leave: 'On leave',
  unavailable: 'Unavailable',
};

// Helper to match barrister names loosely (works with API or local data)
function matchesBarristerName(candidate: string | undefined | null, fullName: string, shortName: string): boolean {
  if (!candidate) return false;
  if (candidate === fullName || candidate === shortName) return true;
  // Strip post-nominals (KC, QC, etc.) for fuzzy matching
  const strip = (s: string) => s.replace(/\s+(KC|QC|SC|MP|CBE|DBE|OBE)$/g, '').trim();
  const c = strip(candidate);
  const f = strip(fullName);
  const s = strip(shortName);
  if (c === f || (s && c === s)) return true;
  // Last name matching
  const cLast = c.split(' ').pop() || '';
  const fLast = f.split(' ').pop() || '';
  return !!cLast && !!fLast && cLast.toLowerCase() === fLast.toLowerCase();
}

export default function BarristerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [activeTab, setActiveTab] = useState<Tab>('calendar');
  const [assigningClerk, setAssigningClerk] = useState(false);
  const [selectedClerkId, setSelectedClerkId] = useState('');
  const [selectedRole, setSelectedRole] = useState<'primary' | 'assistant'>('assistant');
  const qc = useQueryClient();

  const scope: 'admin' | 'clerk' = pathname.startsWith('/clerk/') ? 'clerk' : 'admin';
  const base = scope === 'clerk' ? '/clerk' : '/admin';

  const clerksQ = useQuery({
    queryKey: ['barrister-clerks', id],
    queryFn: () => fetchClerksForBarrister(id!),
    enabled: !!id,
  });

  const availableClerksQ = useQuery({
    queryKey: ['barrister-available-clerks', id],
    queryFn: () => fetchAvailableClerks(id!),
    enabled: !!id && assigningClerk,
  });

  // Fetch barrister from real API
  const barristerQ = useQuery({
    queryKey: ['barrister', id],
    queryFn: () => fetchBarrister(id!),
    enabled: !!id,
  });
  const b = barristerQ.data;

  const allMatters = useMattersStore((s) => s.matters);
  const fetchMatters = useMattersStore((s) => s.fetchMatters);
  const allHearings = useHearingsStore((s) => s.hearings);
  const fetchHearings = useHearingsStore((s) => s.fetchHearings);
  useEffect(() => { fetchMatters(); fetchHearings(); }, [fetchMatters, fetchHearings]);
  const allEvents = useDiaryStore((s) => s.events);
  const user = useAuthStore((s) => s.user);

  const clerkName = user ? `${user.firstName} ${user.lastName}` : '';
  const { managedBarristers } = useClerkWorkspace(scope === 'clerk');

  const principal: PrincipalLite = {
    name: clerkName,
    role: user?.memberType === 'admin' ? 'admin' : scope === 'clerk' ? 'clerk' : 'barrister',
    memberType: user?.memberType,
  };
  const viewerIsAdmin = isAdmin(principal);
  const isClerkView = scope === 'clerk' && !viewerIsAdmin;

  const allBarristerMatters = useMemo(() => {
    if (!b) return [];
    return allMatters.filter((m) => matchesBarristerName(m.barrister, b.fullName, b.shortName));
  }, [allMatters, b]);

  const matters = useMemo(() => {
    if (!isClerkView) return allBarristerMatters;
    return allBarristerMatters.filter((m) => canSeeMatter(principal, m, managedBarristers));
  }, [allBarristerMatters, isClerkView, principal, managedBarristers]);

  const hearings = useMemo(() => {
    if (!b) return [];
    const barristerHearings = allHearings
      .filter((h) => matchesBarristerName(h.leadBarrister, b.fullName, b.shortName))
      .sort((a, z) => (a.date < z.date ? 1 : -1));
    if (!isClerkView) return barristerHearings;
    return barristerHearings.filter((h) => {
      if (!h.matterId) return false;
      const matter = allMatters.find((m) => m.id === h.matterId);
      return canSeeMatter(principal, matter, managedBarristers);
    });
  }, [allHearings, allMatters, b, isClerkView, principal, managedBarristers]);

  const upcomingHearings = useMemo(() => {
    const today = getTodayKey();
    return hearings
      .filter((h) => h.date >= today && h.status !== 'completed' && h.status !== 'vacated')
      .sort((a, z) => (a.date < z.date ? -1 : 1));
  }, [hearings]);

  const weekDays = useMemo(() => {
    const days: { date: string; dayLabel: string; dayNum: number; isToday: boolean }[] = [];
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      days.push({
        date: dateKey(d),
        dayLabel: d.toLocaleDateString('en-GB', { weekday: 'short' }),
        dayNum: d.getDate(),
        isToday: i === 0,
      });
    }
    return days;
  }, []);

  const barristerEvents = useMemo(() => {
    if (!b) return [];
    return allEvents.filter((e) => matchesBarristerName(e.barrister, b.fullName, b.shortName));
  }, [allEvents, b]);

  const eventsByDate = useMemo(() => {
    const m = new Map<string, typeof barristerEvents>();
    for (const e of barristerEvents) {
      const arr = m.get(e.date) ?? [];
      arr.push(e);
      m.set(e.date, arr);
    }
    return m;
  }, [barristerEvents]);

  if (!b) {
    return (
      <div>
        <button
          onClick={() => navigate(-1)}
          className="mb-3 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Back
        </button>
        <Card className="py-16 text-center">
          <CardContent>
            <p className="text-lg font-semibold text-foreground">Barrister not found</p>
            <p className="mt-1 text-sm text-muted-foreground">ID: {id ?? '—'}</p>
            <Button variant="outline" size="md" className="mt-4" onClick={() => navigate(`${base}/barristers`)}>
              Back to barristers
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const activeMattersCount = matters.filter((m) => m.status === 'active' || m.status === 'listed').length;
  const availLabel = AVAILABILITY_LABEL[b.availability] || b.availability;

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-3.5 w-3.5" /> Back to barristers
      </button>

      {/* ── Identity header ── */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col items-start gap-5 sm:flex-row">
            <Avatar src={b.user.avatarUrl ?? undefined} fallback={b.initials} size="xl" className="h-20 w-20 text-xl" />
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                {ROLE_LABEL[b.user.memberType] || b.user.memberType}
              </div>
              <h2 className="mt-1 text-2xl font-semibold tracking-tight text-foreground">{b.fullName}</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {b.callYear ? `Called ${b.callYear}` : 'Call year unknown'}
                {b.silkYear && ` · Took silk ${b.silkYear}`}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <a href={`mailto:${b.user.email}`} className="flex items-center gap-1.5 hover:text-foreground">
                  <Mail className="h-3.5 w-3.5" /> {b.user.email}
                </a>
                {b.user.phone && (
                  <a href={`tel:${b.user.phone}`} className="flex items-center gap-1.5 hover:text-foreground">
                    <Phone className="h-3.5 w-3.5" /> {b.user.phone}
                  </a>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={b.availability === 'available' || b.availability === 'partial' ? 'success' : b.availability === 'on_leave' ? 'default' : 'warning'}>
                {availLabel}
              </Badge>
              {b.publicProfile && b.publicSlug && (
                <Button variant="outline" size="sm" leftIcon={<Globe className="h-3.5 w-3.5" />}
                  onClick={() => window.open(`/barristers/${b.publicSlug}`, '_blank')}>
                  Profile
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── KPI cards ── */}
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard icon={<Briefcase className="h-4 w-4" />} label="Active matters" value={activeMattersCount}
          hint={matters.length > 0 ? `${matters.length} total` : undefined} />
        <StatCard icon={<TrendingUp className="h-4 w-4" />} label="Seniority" value={`${b.seniority} yrs`} />
        <StatCard icon={<Wallet className="h-4 w-4" />} label="Practice areas" value={b.practiceAreas.length}
          hint={b.inn ? `${b.inn}` : undefined} />
      </div>

      {/* ── Tab bar ── */}
      <div className="flex gap-1 border-b border-border">
        <TabButton active={activeTab === 'calendar'} onClick={() => setActiveTab('calendar')}>
          Calendar
        </TabButton>
        <TabButton active={activeTab === 'practice'} onClick={() => setActiveTab('practice')}>
          Practice
        </TabButton>
        <TabButton active={activeTab === 'matters'} onClick={() => setActiveTab('matters')} badge={activeMattersCount || undefined}>
          Matters
        </TabButton>
        <TabButton active={activeTab === 'hearings'} onClick={() => setActiveTab('hearings')} badge={upcomingHearings.length || undefined}>
          Hearings
        </TabButton>
        <TabButton active={activeTab === 'clerks'} onClick={() => setActiveTab('clerks')} badge={clerksQ.data?.length || undefined}>
          Clerks
        </TabButton>
      </div>

      {/* ═══════════════════════════════════════════════
          TAB: Calendar — 7-day view
         ═══════════════════════════════════════════════ */}
      {activeTab === 'calendar' && (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-brand-600 dark:text-brand-400" />
                  Next 7 days
                </CardTitle>
                <CardDescription>
                  {barristerEvents.length} upcoming event{barristerEvents.length === 1 ? '' : 's'}
                </CardDescription>
              </div>
              <Link to={scope === 'clerk' ? '/clerk/diary' : '/admin/calendar'} className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300">
                Open full diary
              </Link>
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="sticky left-0 bg-muted/30 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Day
                    </th>
                    {HOURS.slice(0, 8).map((h) => (
                      <th key={h} className="px-2 py-2 text-center text-[10px] font-semibold text-muted-foreground">{h.slice(0, 2)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {weekDays.map((d) => {
                    const dayEvents = eventsByDate.get(d.date) ?? [];
                    return (
                      <tr key={d.date} className={d.isToday ? 'bg-brand-50/30' : ''}>
                        <td className="sticky left-0 whitespace-nowrap bg-card px-3 py-2 font-medium text-foreground">
                          <span className="text-[10px] text-muted-foreground">{d.dayLabel}</span>{' '}
                          <span className={d.isToday ? 'text-brand-700 dark:text-brand-300' : ''}>{d.dayNum}</span>
                        </td>
                        {HOURS.slice(0, 8).map((_, hourIdx) => {
                          const blockEvent = dayEvents.find((e) => e.start <= hourIdx && e.start + e.span > hourIdx);
                          if (!blockEvent) {
                            return (
                              <td key={hourIdx} className="px-1 py-2">
                                <div className="mx-auto h-7 rounded-md bg-muted/40" />
                              </td>
                            );
                          }
                          const canSee = !isClerkView || canSeeEventContent(principal, blockEvent, allMatters, managedBarristers);
                          const displayLabel = canSee ? blockEvent.title : redactedEventLabel(blockEvent);
                          const isStart = blockEvent.start === hourIdx;
                          return (
                            <td key={hourIdx} className="px-1 py-2"
                              title={canSee ? blockEvent.title : `${redactedEventLabel(blockEvent)} — restricted`}>
                              <div className={`h-7 rounded-md border text-[9px] font-semibold leading-7 ${TONE_STYLE[blockEvent.tone]} truncate px-1 ${canSee ? '' : 'opacity-70'}`}>
                                {isStart ? displayLabel : ''}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {barristerEvents.length === 0 && (
              <div className="px-6 py-8 text-center text-xs text-muted-foreground">
                No events in the next 7 days.
              </div>
            )}
            {/* Legend */}
            <div className="flex flex-wrap items-center gap-3 border-t border-border px-6 py-3 text-xs text-muted-foreground">
              {(['hearing', 'conference', 'task', 'brief_return', 'personal'] as const).map((k) => {
                const tone = k === 'hearing' ? 'brand' : k === 'conference' ? 'amber' : k === 'task' ? 'violet' : k === 'brief_return' ? 'red' : 'emerald';
                return (
                  <span key={k} className="flex items-center gap-1.5">
                    <span className={`inline-block h-3 w-5 rounded border ${TONE_STYLE[tone]}`} />
                    {KIND_LABEL[k]}
                  </span>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ═══════════════════════════════════════════════
          TAB: Practice — areas and expertise
         ═══════════════════════════════════════════════ */}
      {activeTab === 'practice' && (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            {/* Practice areas */}
            <Card>
              <CardHeader>
                <CardTitle>Practice areas</CardTitle>
              </CardHeader>
              <CardContent>
                {b.practiceAreas.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No practice areas recorded.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {b.practiceAreas.map((p) => (
                      <div key={p.id} className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3">
                        <div className="flex items-center gap-2.5 text-sm text-foreground">
                          <Scale className="h-4 w-4 text-brand-600 dark:text-brand-400" />
                          {p.name}
                        </div>
                        <Badge>{p.expertiseLevel}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Side info */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <Award className="mt-0.5 h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400" />
                  <div>
                    <div className="font-medium">Call year</div>
                    <div className="text-xs text-muted-foreground">{b.callYear ?? 'Unknown'}</div>
                  </div>
                </div>
                {b.silkYear && (
                  <div className="flex items-start gap-2">
                    <Award className="mt-0.5 h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400" />
                    <div>
                      <div className="font-medium">Silk year</div>
                      <div className="text-xs text-muted-foreground">{b.silkYear}</div>
                    </div>
                  </div>
                )}
                {b.inn && (
                  <div className="flex items-start gap-2">
                    <Award className="mt-0.5 h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400" />
                    <div>
                      <div className="font-medium">Inn</div>
                      <div className="text-xs text-muted-foreground">{b.inn}</div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Seniority</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1.5">
                  <Badge>{b.seniority} years at the bar</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Quick actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" size="sm" className="w-full justify-start"
                  onClick={() => navigate(scope === 'clerk' ? '/clerk/diary' : '/admin/calendar')}>
                  <Calendar className="h-4 w-4" /> Open diary
                </Button>
                <Button variant="outline" size="sm" className="w-full justify-start"
                  onClick={() => navigate(`${base}/hearings`)}>
                  <Gavel className="h-4 w-4" /> Court list
                </Button>
                <Button variant="outline" size="sm" className="w-full justify-start"
                  onClick={() => navigate(`${base}/fees`)}>
                  <Wallet className="h-4 w-4" /> Fees
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════
          TAB: Matters
         ═══════════════════════════════════════════════ */}
      {activeTab === 'matters' && (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle>Matters</CardTitle>
                <CardDescription>
                  {matters.length > 0
                    ? `${matters.length} matter${matters.length === 1 ? '' : 's'} assigned`
                    : 'No matters assigned yet'}
                </CardDescription>
              </div>
              <Link to={`${base}/matters`} className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300">
                View all
              </Link>
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            {matters.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <Briefcase className="mx-auto h-8 w-8 text-muted-foreground/40" />
                <p className="mt-3 text-sm text-muted-foreground">This barrister has no matters yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {/* A table cannot reflow: on a phone its right-hand columns ran off the screen with no way to reach them. */}
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Matter</th>
                      <th>Court</th>
                      <th>Status</th>
                      <th>Next</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matters.map((c) => (
                      <tr key={c.id} className="cursor-pointer" onClick={() => navigate(`${base}/matters/${c.id}`)}>
                        <td className="font-medium text-foreground">{c.name}</td>
                        <td className="text-muted-foreground">{c.court}</td>
                        <td>
                          <Badge variant={c.status === 'active' ? 'success' : c.status === 'listed' ? 'info' : 'warning'}>
                            {c.status.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="text-xs text-muted-foreground">{c.next}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ═══════════════════════════════════════════════
          TAB: Hearings
         ═══════════════════════════════════════════════ */}
      {activeTab === 'hearings' && (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle>Upcoming hearings</CardTitle>
                <CardDescription>{upcomingHearings.length} scheduled</CardDescription>
              </div>
              <Link to={`${base}/hearings`} className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300">
                View all
              </Link>
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            {upcomingHearings.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <Gavel className="mx-auto h-8 w-8 text-muted-foreground/40" />
                <p className="mt-3 text-sm text-muted-foreground">No upcoming hearings scheduled.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {/* A table cannot reflow: on a phone its right-hand columns ran off the screen with no way to reach them. */}
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Matter</th>
                      <th>Court</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingHearings.slice(0, 10).map((h) => (
                      <tr key={h.id} className="cursor-pointer" onClick={() => navigate(`${base}/hearings/${h.id}`)}>
                        <td className="text-xs text-foreground">{h.date}</td>
                        <td className="text-xs text-brand-700 dark:text-brand-300">{h.time}</td>
                        <td className="font-medium text-foreground">{h.matterName}</td>
                        <td className="text-xs text-muted-foreground">{h.court}</td>
                        <td><Badge>{h.type}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {/* ═══════════════════════════════════════════════
          TAB: Clerks — who manages this barrister
         ═══════════════════════════════════════════════ */}
      {activeTab === 'clerks' && (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-brand-600 dark:text-brand-400" />
                  Clerk assignments
                </CardTitle>
                <CardDescription>
                  {clerksQ.data?.length
                    ? `${clerksQ.data.length} clerk${clerksQ.data.length === 1 ? '' : 's'} manage this barrister`
                    : 'No clerks assigned yet'}
                </CardDescription>
              </div>
              {scope === 'admin' && (
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Plus className="h-3.5 w-3.5" />}
                  onClick={() => setAssigningClerk(true)}
                >
                  Assign clerk
                </Button>
              )}
            </div>
          </CardHeader>

          {/* Assign form */}
          {assigningClerk && (
            <div className="border-b border-border bg-muted/30 px-6 py-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px]">
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Clerk</label>
                  <select
                    className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none"
                    value={selectedClerkId}
                    onChange={(e) => setSelectedClerkId(e.target.value)}
                  >
                    <option value="">Select a clerk…</option>
                    {availableClerksQ.data?.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.firstName} {c.lastName} ({c.memberType.replace('_', ' ')})
                      </option>
                    ))}
                    {availableClerksQ.isLoading && <option disabled>Loading…</option>}
                    {availableClerksQ.data?.length === 0 && <option disabled>All clerks already assigned</option>}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Role</label>
                  <select
                    className="h-9 rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value as 'primary' | 'assistant')}
                  >
                    <option value="assistant">Assistant</option>
                    <option value="primary">Primary</option>
                  </select>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!selectedClerkId}
                  onClick={async () => {
                    if (!id || !selectedClerkId) return;
                    try {
                      await assignClerkToBarrister(id, selectedClerkId, selectedRole);
                      await qc.invalidateQueries({ queryKey: ['barrister-clerks', id] });
                      await qc.invalidateQueries({ queryKey: ['barrister-available-clerks', id] });
                      setAssigningClerk(false);
                      setSelectedClerkId('');
                      toast.success('Clerk assigned');
                    } catch {
                      toast.error('Failed to assign clerk');
                    }
                  }}
                >
                  Assign
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setAssigningClerk(false); setSelectedClerkId(''); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <CardContent className="px-0 pb-0">
            {clerksQ.isLoading && (
              <div className="px-6 py-10 text-center text-sm text-muted-foreground">Loading…</div>
            )}
            {!clerksQ.isLoading && clerksQ.data?.length === 0 && (
              <div className="px-6 py-12 text-center">
                <Users className="mx-auto h-8 w-8 text-muted-foreground/40" />
                <p className="mt-3 text-sm text-muted-foreground">No clerks assigned to this barrister.</p>
                {scope === 'admin' && (
                  <Button variant="outline" size="sm" className="mt-4" onClick={() => setAssigningClerk(true)}>
                    Assign a clerk
                  </Button>
                )}
              </div>
            )}
            {(clerksQ.data?.length ?? 0) > 0 && (
              <div className="overflow-x-auto">
                {/* A table cannot reflow: on a phone its right-hand columns ran off the screen with no way to reach them. */}
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role in chambers</th>
                      <th>Assignment</th>
                      <th>Since</th>
                      {scope === 'admin' && <th className="w-10"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {clerksQ.data?.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <div className="flex items-center gap-2.5">
                            <Avatar
                              src={a.clerk.avatarUrl ?? undefined}
                              fallback={`${a.clerk.firstName[0]}${a.clerk.lastName[0]}`}
                              size="sm"
                            />
                            <span className="font-medium text-foreground">
                              {a.clerk.firstName} {a.clerk.lastName}
                            </span>
                          </div>
                        </td>
                        <td className="text-muted-foreground">{a.clerk.email}</td>
                        <td><Badge>{a.clerk.memberType.replace('_', ' ')}</Badge></td>
                        <td>
                          <span className="flex items-center gap-1.5">
                            {a.role === 'primary' && <Star className="h-3.5 w-3.5 text-amber-500" weight="fill" />}
                            <Badge variant={a.role === 'primary' ? 'gold' : 'default'}>
                              {a.role}
                            </Badge>
                          </span>
                        </td>
                        <td className="text-xs text-muted-foreground">
                          {new Date(a.assignedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        {scope === 'admin' && (
                          <td>
                            <button
                              className="rounded p-1 text-muted-foreground hover:bg-red-100 dark:hover:bg-red-500/20 dark:bg-red-500/15 hover:text-red-600 dark:hover:text-red-400"
                              title="Remove assignment"
                              onClick={async () => {
                                if (!id) return;
                                try {
                                  await removeClerkFromBarrister(id, a.clerk.id);
                                  await qc.invalidateQueries({ queryKey: ['barrister-clerks', id] });
                                  await qc.invalidateQueries({ queryKey: ['barrister-available-clerks', id] });
                                  toast.success('Clerk removed');
                                } catch {
                                  toast.error('Failed to remove clerk');
                                }
                              }}
                            >
                              <Trash className="h-4 w-4" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function TabButton({ active, onClick, badge, children }: {
  active: boolean;
  onClick: () => void;
  badge?: number;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative px-4 py-3 text-sm font-medium transition-colors ${
        active
          ? 'border-b-2 border-brand-600 text-brand-600 dark:text-brand-400'
          : 'border-b-2 border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
      {badge != null && badge > 0 && (
        <span className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-100 dark:bg-red-500/15 px-1.5 text-[10px] font-bold text-red-700 dark:text-red-300">
          {badge}
        </span>
      )}
    </button>
  );
}
